/** Pull a trailing repeat phrase off a task's text — tasks plan D6, the
 *  sibling of `dueParse` with the same rules: a *trailing* phrase only,
 *  English and the German Doug also types, the dangling word stripped.
 *
 *  "Get wine every week"   → { body: 'Get wine', repeat: { every: 1, unit: 'week' } }
 *  "Weed garden every 10 days" → { body: 'Weed garden', repeat: { every: 10, unit: 'day' } }
 *  "Pay rent every month"  → { body: 'Pay rent every month', repeat: null }  (out of scope: the words stay)
 *
 *  No imports, so the root suite loads it directly. */

export interface Repeat { every: number; unit: 'day' | 'week' }

const trimSeparator = (s: string) => s.replace(/[\s,–-]+$/, '').trim()

/** One word, one meaning. */
const WORDS: Record<string, Repeat> = {
  daily: { every: 1, unit: 'day' },
  täglich: { every: 1, unit: 'day' },
  weekly: { every: 1, unit: 'week' },
  wöchentlich: { every: 1, unit: 'week' },
  fortnightly: { every: 2, unit: 'week' },
}

const UNIT: Record<string, 'day' | 'week'> = {
  day: 'day', days: 'day', tag: 'day', tage: 'day', tagen: 'day',
  week: 'week', weeks: 'week', woche: 'week', wochen: 'week',
}

export function repeatParse(text: string): { body: string; repeat: Repeat | null } {
  const trimmed = text.trim()
  const keep = { body: trimmed, repeat: null }

  // "daily", "weekly", "fortnightly", "täglich", "wöchentlich"
  const word = trimmed.match(/^(.*?)(?:^|\s)([A-Za-zäöü]+)\.?$/)
  if (word && WORDS[word[2].toLowerCase()]) {
    const body = trimSeparator(word[1])
    return body ? { body, repeat: WORDS[word[2].toLowerCase()] } : keep
  }

  // "every day", "every 2 weeks", "every other week", "alle 3 Tage", "jede Woche"
  const phrase = trimmed.match(/^(.*?)(?:^|\s)(?:every|alle|jede[nrs]?)\s+(?:(\d{1,3}|other|zwei)\s+)?([A-Za-zäöü]+)\.?$/i)
  if (phrase) {
    const unit = UNIT[phrase[3].toLowerCase()]
    if (!unit) return keep // "every month", "every Monday": out of scope, the words stay
    const n = phrase[2] === undefined ? 1 : /^(other|zwei)$/i.test(phrase[2]) ? 2 : Number(phrase[2])
    if (!(n >= 1 && n <= 365)) return keep
    const body = trimSeparator(phrase[1])
    return body ? { body, repeat: { every: n, unit } } : keep
  }

  return keep
}

/** The words for a rule: "every day", "weekly", "every 2 weeks". */
export function repeatLabel(r: Repeat): string {
  if (r.every === 1) return r.unit === 'day' ? 'every day' : 'weekly'
  return `every ${r.every} ${r.unit}s`
}
